import { z } from 'zod';
import { AdsId } from './ads-builder.js';
/*
 * «Окупаемость» (`/api/ads/payback/*`). Цифры прогноза — ТОЛЬКО из старого
 * auto-ads `/internal/analytics/payback-predictions` (после вычетов), agent API —
 * только детализация без ROAS (спека Р1/Р2). Деньги — центы USD (`*Minor`, `cpp`).
 * Схемы `z.object` — лишние поля auto-ads отбрасываются на выходе admin-api.
 */
export const ADS_PAYBACK_MAX_WINDOW_DAYS = 366;
export const ADS_PAYBACK_MAX_DAYS_WINDOW = 92;
export const ADS_PAYBACK_MARKER_KEYS = [
    'trial_price_tier',
    'trial_choice',
    'age_bucket',
    'gender',
    'region',
    'card_funding',
    'first_attempt_failed',
    'upsell24_any',
    'cancel24',
    'first_cycle_paid_14',
    'country',
    'card_brand',
    'three_ds',
];
/** `YYYY-MM-DD` существующего дня: `2026-02-30` отвергается (Date.parse молча сдвинул бы на 2 марта). */
const isRealDay = (v) => {
    const ms = Date.parse(`${v}T00:00:00Z`);
    return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === v;
};
export const AdsPaybackDay = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Неверная дата' })
    .refine(isRealDay, { message: 'Неверная дата' });
export const AdsPaybackCampaignId = z
    .string()
    .regex(/^\d{1,30}$/, { message: 'Неверная кампания' });
export const AdsPaybackMoment = z.enum(['t0', 't24', 'd14']);
export const AdsPaybackMomentOrBest = z.enum(['best', 't0', 't24', 'd14']);
export const AdsPaybackMomentUsed = z.enum(['t0', 't24', 'd14', 'mixed']);
const nnum = z.number().nullable();
const spanDays = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
const AccountsCsv = z
    .string()
    .optional()
    .transform((s) => s
    ? Array.from(new Set(s
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)))
    : [])
    .pipe(z.array(AdsId).max(100));
const PeriodQueryShape = {
    from: AdsPaybackDay,
    to: AdsPaybackDay,
    accounts: AccountsCsv,
};
const withPeriodChecks = (s, maxDays) => s
    .refine((q) => q.from <= q.to, { message: 'Начало периода позже конца', path: ['from'] })
    .refine((q) => spanDays(q.from, q.to) <= maxDays, {
    message: `Период длиннее ${maxDays} дней`,
    path: ['to'],
});
// ---------- запросы ----------
const RevisionNo = z.coerce.number().int().min(1);
export const AdsPaybackReportQuery = z
    .object({
    ...PeriodQueryShape,
    moment: AdsPaybackMomentOrBest.default('best'),
    model: z.string().min(1).max(64).optional(),
    includeDays: z.enum(['1']).optional(),
    /** S3: `revision` — кампании с несколькими версиями настроек воронки делятся на строки «кампания · версия». */
    split: z.enum(['none', 'revision']).optional(),
    /** Номер версии; только с `campaignId` и `split=revision`. Отдельный параметр, не часть ключа. */
    campaignId: AdsPaybackCampaignId.optional(),
    revision: RevisionNo.optional(),
})
    .refine((q) => q.from <= q.to, { message: 'Начало периода позже конца', path: ['from'] })
    .refine((q) => q.revision === undefined || q.campaignId !== undefined, {
    message: 'Версия задаётся только вместе с кампанией',
    path: ['revision'],
})
    .refine((q) => q.revision === undefined || q.split === 'revision', {
    message: 'Версия задаётся только при разделении по версиям',
    path: ['revision'],
})
    .refine((q) => spanDays(q.from, q.to) <=
    (q.includeDays ? ADS_PAYBACK_MAX_DAYS_WINDOW : ADS_PAYBACK_MAX_WINDOW_DAYS), { message: 'Период слишком длинный', path: ['to'] });
export const AdsPaybackCampaignMarkersQuery = withPeriodChecks(z.object({
    ...PeriodQueryShape,
    moment: AdsPaybackMomentOrBest.default('best'),
    model: z.string().min(1).max(64).optional(),
    campaignId: AdsPaybackCampaignId,
    /** S3: разрез внутри одной версии настроек (`split=revision` + `revision=N`). */
    split: z.enum(['none', 'revision']).optional(),
    revision: RevisionNo.optional(),
}), ADS_PAYBACK_MAX_WINDOW_DAYS);
export const AdsPaybackCampaignDaysQuery = withPeriodChecks(z.object({
    ...PeriodQueryShape,
    /** Версия модели — уходит в agent `payback-summary` как `modelVersion`. */
    model: z.string().min(1).max(64).optional(),
    campaignId: AdsPaybackCampaignId,
}), ADS_PAYBACK_MAX_DAYS_WINDOW);
export const AdsPaybackCampaignOutcomesQuery = withPeriodChecks(z.object({ ...PeriodQueryShape, campaignId: AdsPaybackCampaignId }), ADS_PAYBACK_MAX_WINDOW_DAYS);
// ---------- блоки отчёта ----------
export const AdsPaybackInterval = z.object({
    available: z.boolean(),
    reason: z.string().nullable(),
    lo: nnum,
    hi: nnum,
});
export const AdsPaybackVerdictTerm = z.object({
    verdict: z.enum(['нет расхода', 'мало данных', 'окупится', 'не окупится', 'серая зона']),
    firm: z.boolean(),
});
/** После вычетов (`withComponents`): либо блок, либо «недоступно» с причиной. */
export const AdsPaybackWithComponents = z.union([
    z.object({ available: z.literal(false), reason: z.string() }),
    z.object({
        roas3: nnum,
        interval3: AdsPaybackInterval,
        p3_ge_with_fees: nnum.optional().default(null),
        verdict3_with_fees: AdsPaybackVerdictTerm.optional(),
    }),
]);
/** Горизонт 60/90/120: `roas`/`interval` — ДО вычетов, `withComponents` — ПОСЛЕ. */
export const AdsPaybackHorizon = z.object({
    roas: nnum,
    interval: AdsPaybackInterval,
    p_ge_with_fees: nnum,
    withComponents: AdsPaybackWithComponents,
});
export const AdsPaybackFactHorizon = z.object({
    factRoas: nnum,
    factN: z.number().int(),
    factShare: nnum,
    predRoasMatured: nnum,
});
export const AdsPaybackAnswer = z.object({
    momentUsed: AdsPaybackMomentUsed,
    coveragePct: nnum,
    theta: z.number(),
    verdict: z.enum(['yes', 'unclear', 'no']).nullable(),
    preliminary: z.boolean(),
    roas3: nnum,
    interval: z.object({ lo: nnum, hi: nnum }).nullable(),
    p: nnum,
});
export const AdsPaybackGroup = z.object({
    purchases: z.number().int(),
    purchasesWithPrediction: z.number().int(),
    coverage: nnum,
    spendMinorFull: z.number(),
    cpp: nnum,
    factRevenueMinor: z.number(),
    factRoas: nnum,
    avgAgeDays: nnum,
    cppBreakevenMinor: nnum.optional().default(null),
    cppSafeMinor: nnum.optional().default(null),
    answer: AdsPaybackAnswer,
    horizons: z.object({
        '60': AdsPaybackHorizon,
        '90': AdsPaybackHorizon,
        '120': AdsPaybackHorizon,
    }),
    factHorizons: z
        .object({
        '14': AdsPaybackFactHorizon.optional(),
        '30': AdsPaybackFactHorizon.optional(),
        '60': AdsPaybackFactHorizon.optional(),
        '90': AdsPaybackFactHorizon.optional(),
        '120': AdsPaybackFactHorizon.optional(),
    })
        .optional()
        .default({}),
    momentMix: z.record(AdsPaybackMoment, z.number().int()).optional(),
});
export const AdsPaybackCampaign = AdsPaybackGroup.extend({
    campaignId: z.string(),
    accountId: z.string(),
    /** Зависимость Р11: нет поля или null → UI «Кампания без названия». */
    campaignName: z.string().nullable().optional().default(null),
    accountName: z.string().nullable().optional().default(null),
    spendShare: nnum,
    /**
     * S3: поля версий настроек воронки. Все необязательные и БЕЗ значений по умолчанию: пока auto-ads
     * их не шлёт (флаг выкл), ответ admin-api побайтно прежний. Строки-сегменты `split=revision` несут
     * `revision`, `settingsId`, `revisionWindow`, `mixedDays`, `spendEstimated`, `spendWithoutPurchases`.
     */
    settingsChangedInPeriod: z.boolean().nullish().catch(undefined),
    revision: z.number().int().nullish().catch(undefined),
    settingsId: z.string().nullish().catch(undefined),
    revisionWindow: z
        .object({ from: z.string(), to: z.string().nullable().catch(null) })
        .nullish()
        .catch(undefined),
    mixedDays: z.array(z.string()).nullish().catch(undefined),
    spendEstimated: z.boolean().nullish().catch(undefined),
    /** Расход дней, где у версии не было покупок (центы). */
    spendWithoutPurchases: z.number().nullish().catch(undefined),
});
export const AdsPaybackAccount = AdsPaybackGroup.extend({
    accountId: z.string(),
    accountName: z.string().nullable().optional().default(null),
    spendShare: nnum,
});
export const AdsPaybackMarker = z.union([
    z.object({ available: z.literal(true), values: z.record(z.string(), AdsPaybackGroup) }),
    z.object({ available: z.literal(false), reason: z.string() }),
]);
export const AdsPaybackPeriodRow = z.object({
    key: z.string(),
    from: z.string(),
    to: z.string(),
    partial: z.boolean(),
    momentUsed: AdsPaybackMomentUsed,
    coveragePct: nnum,
    group: AdsPaybackGroup,
});
export const AdsPaybackPeriods = z.object({
    weeks: z.array(AdsPaybackPeriodRow),
    months: z.array(AdsPaybackPeriodRow),
    /** null — auto-ads без `includeDays` (спека §9.2) или день не запрошен. */
    days: z.array(AdsPaybackPeriodRow).nullable().optional().default(null),
    total: AdsPaybackPeriodRow,
    momentMix: z.record(AdsPaybackMoment, z.number().int()).optional(),
});
export const AdsPaybackMeta = z.object({
    from: z.string(),
    to: z.string(),
    asOf: z.string().nullable(),
    modelVersion: z.string().nullable(),
    activeVersionSource: z.enum(['explicit-param', 'config', 'latest-scoredAt']).optional(),
    latestScoredVersion: z.string().nullable().optional().default(null),
    availableModelVersions: z.array(z.string()).optional().default([]),
    levelNote: z.string(),
    thresholds: z.object({ main: z.number(), withFees: z.number() }),
    levels: z
        .array(z.object({ accountId: z.string().nullable(), moment: z.string() }))
        .optional()
        .default([]),
    lateShare: nnum.optional().default(null),
    incompleteShare: nnum.optional().default(null),
    reconciliation: z.object({
        purchaseEvents: z.number().int(),
        withPredictions: z.number().int(),
        missing: z.number().int(),
        extraPredictions: z.number().int(),
        missingShare: nnum,
    }),
    /**
     * S3: версии настроек воронки >1, вступившие в силу внутри периода (по всем кампаниям скоупа,
     * не больше 200). Нет поля (флаг auto-ads выкл) — в ответе его тоже нет: метки не рисуются,
     * переключатель разделения скрыт.
     */
    settingsChanges: z
        .array(z.object({
        campaignId: z.string(),
        campaignName: z
            .string()
            .nullish()
            .catch(null)
            .transform((v) => v ?? null),
        revision: z.number().int(),
        effectiveFrom: z.string(),
        source: z
            .object({
            kind: z.string().catch('manual'),
            via: z
                .string()
                .nullish()
                .catch(null)
                .transform((v) => v ?? null),
        })
            .nullish()
            .catch(null)
            .transform((v) => v ?? { kind: 'manual', via: null }),
        reason: z
            .string()
            .nullish()
            .catch(null)
            .transform((v) => v ?? null),
        changedKeys: z
            .array(z.string())
            .nullish()
            .catch(null)
            .transform((v) => v ?? []),
    }))
        .nullish()
        .catch(undefined),
    settingsChangesTruncated: z.boolean().nullish().catch(undefined),
    /** `split=revision`: версии неразделённых кампаний с настройками (одна версия в периоде). */
    unsplitRevisions: z
        .array(z.object({ campaignId: z.string(), revision: z.number().int() }))
        .nullish()
        .catch(undefined),
});
export const AdsPaybackReport = z.object({
    /** Под `moment=best` auto-ads отдаёт `'mixed'`, если покупки выбрали разные моменты (прод 30 дн / 12 нед). */
    moment: AdsPaybackMomentUsed,
    now: z.string(),
    preliminaryDays: z.array(z.string()),
    preliminary: z.boolean(),
    spendWithoutPurchasesMinor: z.number(),
    total: AdsPaybackGroup,
    campaigns: z.array(AdsPaybackCampaign),
    accountsBreakdown: z.array(AdsPaybackAccount),
    markers: z.record(z.string(), AdsPaybackMarker),
    periods: AdsPaybackPeriods,
    reconciliation: z.object({
        spendReconciles: z.boolean(),
        expectedReconciles: z.boolean(),
        markerSpendReconciles: z.boolean().nullable(),
    }),
    meta: AdsPaybackMeta,
});
export const AdsPaybackCampaignMarkers = z.object({
    campaignId: z.string(),
    markers: z.record(z.string(), AdsPaybackMarker),
});
// ---------- детализация (agent API, без ROAS) ----------
export const AdsPaybackCampaignDays = z.object({
    days: z.array(z.object({
        day: AdsPaybackDay,
        spendMinor: z.number().int(),
        purchases: z.number().int(),
        cppMinor: z.number().int().nullable(),
    })),
});
const nint = z.number().int().nullable();
export const AdsPaybackCampaignOutcomes = z
    .object({
    purchases: z.number().int(),
    withOutcome: nint,
    firstRenewal: z
        .object({
        paid: nint,
        failed: nint,
        pending: nint,
        cancelledBefore: nint,
        notApplicable: nint,
    })
        .nullable(),
    cancellations: z.object({ total: nint, beforeFirstCharge: nint }).nullable(),
    refunds: z.object({ count: nint, amountMinor: nint }).nullable(),
    disputesOpened: nint,
    chargeblastAlerts: nint,
    statusNow: z.record(z.string(), z.number().int()),
    statusAsOf: z.string().nullable(),
})
    .nullable();
export const AdsPaybackFreshness = z.object({
    snapshotsFinalizedThrough: z.string().nullable(),
    predictionsScoredAt: z.string().nullable(),
    factsAsOf: z.string().nullable(),
    outcomesAsOf: z.string().nullable(),
});
//# sourceMappingURL=ads-payback.js.map